/**
 * Comment-generation access for the Settings modal and the chat popup (#77-#81).
 *
 * The `useScrapers` mold: one shared query key, and every mutation answers with the full #77
 * payload, so the cache is *replaced* rather than invalidated and the tab never flickers through a
 * loading state after a save. Nothing else is invalidated — comment generation is a fenced sidecar
 * (SPEC § Isolation), so a backend config save can touch no other query.
 *
 * The same query is what the chat popup checks before offering a chat, which is why the readiness
 * answer is worth a minute of `staleTime` — the `QK.notion` treatment, for the same reason.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { AiPromptsUpdateBody, AiTabDTO } from '@lw/shared';
import { getAi, putAiActive, putAiBackendConfig, putAiPrompts, testAiBackend } from '../../api/ai';
import { QK } from '../../api/queryKeys';

/** Readiness changes only when the user edits this tab — a minute of staleness costs nothing. */
export const AI_STALE_MS = 60_000;

export function useAiQuery(enabled = true): UseQueryResult<AiTabDTO, Error> {
  return useQuery({
    queryKey: QK.ai,
    queryFn: ({ signal }) => getAi(signal),
    enabled,
    staleTime: AI_STALE_MS,
  });
}

function useAiMutation<TVars>(mutationFn: (vars: TVars) => Promise<AiTabDTO>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (tab) => queryClient.setQueryData(QK.ai, tab),
  });
}

export function useUpdateAiBackendConfig() {
  return useAiMutation(
    (vars: { backendId: string; config: Record<string, string | number | boolean> }) =>
      putAiBackendConfig(vars.backendId, vars.config),
  );
}

export function useSetActiveAiBackend() {
  return useAiMutation((backendId: string | null) => putAiActive(backendId));
}

export function useUpdateAiPrompts() {
  return useAiMutation((body: AiPromptsUpdateBody) => putAiPrompts(body));
}

/** #79 answers `{ok:false}` for a bad key rather than throwing, so this never invalidates. */
export function useTestAiBackend() {
  return useMutation({ mutationFn: (backendId: string) => testAiBackend(backendId) });
}
